// Module catalog. Each module has fields used to build forms + table columns.
import {
  LayoutDashboard, Users, UserPlus, UserMinus, UserCheck, UserSearch, CalendarCheck, CalendarOff, FileText, ClipboardList,
  TrendingUp, ShoppingCart, ShoppingBag, Receipt, ReceiptText, Plane, MapPin, Truck, Boxes, PackageOpen, Laptop, LifeBuoy,
  Building2, Store, BookOpen, FolderArchive, Megaphone, GraduationCap, Wallet, Coins, Banknote, Award, ArrowUpRight,
  PartyPopper, BriefcaseBusiness, PhoneCall, MessageCircle,
} from "lucide-react";

export const MODULE_CATALOG = {
  hr_attendance: {
    // Merged into Team Attendance — this entry is kept only as an alias so any
    // existing /m/hr_attendance link routes to the Team Attendance workflow.
    label: "Team Attendance",
    group: "HR",
    icon: CalendarCheck,
    type: "workflow-team-attendance",
    moduleKey: "hr_attendance",
    hiddenInSidebar: true,
  },
  hr_leaves: {
    label: "Leave Requests",
    group: "HR",
    icon: CalendarOff,
    type: "workflow-leaves",
    moduleKey: "hr_leaves",
  },
  hr: {
    label: "HR",
    group: "HR",
    icon: UserSearch,
    type: "workflow-hr",
    moduleKey: "hr",
  },
  hr_daily_report: {
    label: "Daily Report",
    group: "HR",
    icon: ClipboardList,
    type: "workflow-daily-report",
    moduleKey: "hr_daily_report",
  },
  sales_crm_report: {
    label: "CRM Reporting",
    group: "Sales",
    icon: PhoneCall,
    type: "workflow-crm-reporting",
    moduleKey: "sales_crm_report",
  },
  gem_work: {
    label: "GeM Work",
    group: "Sales",
    icon: Store,
    type: "workflow-gem-work",
    moduleKey: "gem_work",
  },
  chat: {
    label: "Chat",
    group: "Communication",
    icon: MessageCircle,
    type: "workflow-chat",
    moduleKey: "chat",
    hiddenInSidebar: true,
  },
  quotation: {
    label: "Quotation",
    group: "Sales",
    icon: FileText,
    type: "workflow-quotation",
    moduleKey: "quotation",
  },
  sales_orders: {
    label: "Orders",
    group: "Sales",
    icon: ShoppingCart,
    type: "workflow-orders",
    moduleKey: "sales_orders",
  },
  my_sales: {
    label: "My Sales",
    group: "Sales",
    icon: TrendingUp,
    type: "external",
    href: "/my-sales",
    moduleKey: "my_sales",
    hiddenInSidebar: true,
  },
  documentation: {
    label: "Documentation",
    group: "Sales",
    icon: FolderArchive,
    type: "workflow-documentation",
    moduleKey: "documentation",
  },
  dispatch_orders: {
    label: "Dispatch & Logistics",
    group: "Dispatch",
    icon: Truck,
    type: "workflow-dispatches",
    moduleKey: "dispatch_orders",
  },
  // v109.9.11 — Travel Advance / Advance Settlement / Travel Booking are now
  // sub-tabs of the unified `travel` module (see pages/Travel.jsx). They no
  // longer exist as standalone catalog entries. Backend collections
  // (accounts_advances, accounts_claims, travel_booking*) are untouched.
  travel: {
    label: "Travel",
    group: "Accounts",
    icon: Plane,
    type: "workflow-travel",
    moduleKey: "travel",
  },
  daily_expense: {
    label: "Daily Expense",
    group: "Accounts",
    icon: Coins,
    type: "workflow-daily-expense",
    moduleKey: "daily_expense",
  },
  it_assets: {
    label: "Asset Management",
    group: "IT",
    icon: Laptop,
    type: "workflow-assets",
    moduleKey: "it_assets",
  },
  it_tickets: {
    label: "Support Tickets",
    group: "IT",
    icon: LifeBuoy,
    type: "records",
    moduleKey: "it_tickets",
    fields: [
      { key: "title", label: "Issue Title", required: true },
      { key: "category", label: "Category", type: "select", options: ["Hardware", "Software", "Network", "Access", "Other"], required: true },
      { key: "priority", label: "Priority", type: "select", options: ["Low", "Medium", "High", "Critical"], required: true },
      { key: "description", label: "Description", type: "textarea", required: true },
      { key: "status", label: "Status", type: "select", options: ["Open", "In Progress", "Resolved", "Closed"], required: true },
      { key: "assigned_to", label: "Assigned To (IT)" },
    ],
    columns: ["title", "category", "priority", "created_by_name", "assigned_to", "status"],
  },
  crm_clients: {
    label: "Client Database",
    group: "CRM",
    icon: Building2,
    type: "workflow-crm",
    moduleKey: "crm_clients",
  },
  documents: {
    label: "Documents Corner",
    group: "Knowledge",
    icon: BookOpen,
    type: "workflow-documents",
    moduleKey: "documents",
  },
  trainers: {
    label: "Trainers",
    group: "Training",
    icon: GraduationCap,
    type: "workflow-trainers",
    moduleKey: "trainers",
  },
  incentive: {
    label: "Incentive",
    group: "Finance",
    icon: Award,
    type: "workflow-incentive",
    moduleKey: "incentive",
  },
  announcements: {
    label: "Announcements",
    group: "Knowledge",
    icon: Megaphone,
    type: "workflow-announcements",
    moduleKey: "announcements",
  },
  holidays: {
    label: "Holidays",
    group: "HR",
    icon: PartyPopper,
    type: "workflow-holidays",
    moduleKey: "holidays",
  },
  payroll: {
    label: "Payroll",
    group: "Finance",
    icon: Banknote,
    type: "workflow-payroll",
    moduleKey: "payroll",
  },
  vendor_management: {
    label: "Vendor Management",
    group: "Dispatch",
    icon: Store,
    type: "workflow-vendors",
    moduleKey: "vendor_management",
  },
  product_issuance: {
    label: "Product Issuance",
    group: "Dispatch",
    icon: PackageOpen,
    type: "workflow-product-issuance",
    moduleKey: "product_issuance",
  },
  team_attendance: {
    label: "Team Attendance",
    group: "HR",
    icon: CalendarCheck,
    type: "workflow-team-attendance",
    moduleKey: "team_attendance",
    // v109.9.23 — Team Attendance is now consolidated into Payroll →
    // Attendance tab. The route stays alive for legacy deep-links but
    // the entry is hidden from the sidebar.
    hiddenInSidebar: true,
  },
};

export const MODULE_GROUPS = ["Communication", "HR", "Sales", "Accounts", "Dispatch", "IT", "CRM", "Training", "Finance", "Knowledge"];

export const ROLE_LABELS = {
  master_admin: "Master Admin",
  hr_admin: "HR Admin",
  sales_admin: "Sales Admin",
  sales_team: "Sales Team",
  accounts_admin: "Accounts Admin",
  dispatch_admin: "Dispatch Admin",
  it_admin: "IT Admin",
  crm_manager: "CRM Manager",
  documentation_team: "Documentation Team",
  trainer: "Trainer",
  employee: "Employee",
};

export function moduleAccessibleFor(user, key) {
  // v109.9.52 — Master Access RBAC. Master admin sees everything. Non-master
  // users see modules that the Master Access grants (role + overrides +
  // common access) resolved on the backend as `granted_modules`.
  if (!user) return false;
  if (user.role === "master_admin") return true;
  const granted = user.granted_modules || [];
  return granted.includes(key);
}

/** Returns true if the current user can perform `action` on `moduleKey`.
 * `action` ∈ "view" | "edit" | "delete". */
export function moduleCanDo(user, moduleKey, action = "view") {
  if (!user) return false;
  if (user.role === "master_admin") return true;
  const perms = (user.effective_permissions || {})[moduleKey];
  if (!perms) return false;
  if (perms[action]) return true;
  for (const tp of Object.values(perms.tabs || {})) {
    if (tp && tp[action]) return true;
    for (const sp of Object.values(tp?.subtabs || {})) {
      if (sp && sp[action]) return true;
    }
  }
  return false;
}

/** Returns tab-level V/E/D from effective_permissions if available. */
export function tabCanDo(user, moduleKey, tabKey, action = "view") {
  if (!user) return false;
  if (user.role === "master_admin") return true;
  const perms = (user.effective_permissions || {})[moduleKey];
  if (!perms) return false;
  const t = (perms.tabs || {})[tabKey];
  // If no tab-level override, fall back to module-level permission.
  if (!t) return !!perms[action];
  return !!t[action];
}

/** Returns true if a user should see a specific tab of a module.
 *  Strict rules (v109.9.61 — nothing is common by default):
 *   - master_admin → always true
 *   - effective_permissions module-level view/edit/delete → true (full module)
 *   - effective_permissions tab-level view/edit/delete → true
 *   - any sub-tab of that tab granted → true
 *   - otherwise → false (tab hidden)
 *
 *  Legacy `user.modules` list no longer implicitly opens all tabs; tabs must
 *  be granted explicitly via Master Access Common / Roles / Overrides.
 */
// v109.9.158 — Universally accessible tabs. These are the "personal /
// common" tabs of a module that must be visible to every logged-in user
// regardless of role or RBAC grants, matching the product spec that
// travel booking, advance and settlement are common employee actions.
const _COMMON_TABS = {
  travel: new Set(["policy", "advance", "settlement", "booking"]),
  // v109.9.159 — Trainer & Training tabs (Upcoming / Assigned / Completed)
  // must be visible to any user granted the trainers module via the
  // Access button. Admin-only tabs (projects, approval) remain RBAC-gated.
  trainers: new Set(["trainer", "training"]),
};

export function tabAccessibleFor(user, moduleKey, tabKey) {
  if (!user) return false;
  if (user.role === "master_admin") return true;
  if (_COMMON_TABS[moduleKey]?.has(tabKey)) return true;
  const perms = (user.effective_permissions || {})[moduleKey];
  if (!perms) return false;
  if (perms.view || perms.edit || perms.delete) return true;
  const t = (perms.tabs || {})[tabKey];
  if (!t) return false;
  if (t.view || t.edit || t.delete) return true;
  for (const sp of Object.values(t.subtabs || {})) {
    if (sp.view || sp.edit || sp.delete) return true;
  }
  return false;
}

// v109.9.159 — Universally accessible sub-tabs. Training sub-tabs
// (Upcoming / Assigned / Completed) must open for any user granted the
// trainers module — filtering by user identity happens in the page itself.
const _COMMON_SUBTABS = {
  trainers: {
    training: new Set(["upcoming", "assigned", "completed"]),
  },
};

/** Sub-tab visibility check (same strict semantics). */
export function subtabAccessibleFor(user, moduleKey, tabKey, subtabKey) {
  if (!user) return false;
  if (user.role === "master_admin") return true;
  if (_COMMON_SUBTABS[moduleKey]?.[tabKey]?.has(subtabKey)) return true;
  const perms = (user.effective_permissions || {})[moduleKey];
  if (!perms) return false;
  if (perms.view || perms.edit || perms.delete) return true;
  const t = (perms.tabs || {})[tabKey];
  if (!t) return false;
  if (t.view || t.edit || t.delete) return true;
  const s = (t.subtabs || {})[subtabKey];
  return !!(s && (s.view || s.edit || s.delete));
}
